import { useState, useMemo, useEffect, useTransition, useDeferredValue, useRef, useLayoutEffect } from 'react';
 import ProductForm from './ProductForm';
import { db } from '../../firebase';
import { permOf } from '../../lib/permHook';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, limit, startAfter, serverTimestamp } from "firebase/firestore";
import { logAudit } from '../../lib/audit';
import { useProducts } from '../../context/ProductsContext';
import { findProductRecipeDependencies } from '../../lib/productRecipeDependencies';
import { hasRecipeConfiguration } from '../../lib/productStockPolicy';
import { isPromotionProduct } from '../../lib/promotionRules';
import { useSettings } from '../../context/SettingsContext';
import { buildProductExport } from '../../lib/productExport';
import ProductExportDialog from './ProductExportDialog';
import ProductImportWizard from './ProductImportWizard';
import { analyzeProductTransfer, inspectProductTransferFile } from '../../lib/productTransfer/importAnalysis';


const safeUser = (u) =>
  typeof u === 'string'
    ? u
    : (u?.email ?? u?.username ?? u?.displayName ?? u?.uid ?? '');

const sanitizeProduct = (p) => ({
  ...p,
  // normaliza todo lo que puede venir “raro”
  registeredBy: safeUser(p?.registeredBy),
  category: typeof p?.category === 'string' ? p.category : (p?.category?.value ?? ''),
  type: String(p?.type ?? 'final'),
  unit: String(p?.unit ?? ''),
  price: Number(p?.price ?? 0),
  costPrice: Number(p?.costPrice ?? 0),
  stock: Number(p?.stock ?? 0),
  excludeFromEntries: !!p?.excludeFromEntries,
});


// Altura de la barra superior global (ajústala si tu topbar real mide 56/60/72)
const APPBAR_H = 64;


const ProductsView = ({ currentUser, onBack }) => {
  const { settings, loadingSettings } = useSettings();
  const [exportSelection, setExportSelection] = useState(() => new Set());
  const [exportReview, setExportReview] = useState(null);
  const [exportError, setExportError] = useState('');
  const [importingProducts, setImportingProducts] = useState(false);
  const [transferImportAnalysis, setTransferImportAnalysis] = useState(null);
  const [transferImportError, setTransferImportError] = useState('');
  const importInputRef = useRef(null);
  const {
    products,
    setProducts,
    loading: productsLoading,
    loadingAll,
    fullReady,
    hasMore,
    loadMoreProducts,
    loadAllProductsOnce,
  } = useProducts();
  const [salesDocs, setSalesDocs] = useState([]);
const [searchTerm, setSearchTerm] = useState('');
const deferredSearch = useDeferredValue(searchTerm);const [isPending, startTransition] = useTransition();
  const [currentSubView, setCurrentSubView] = useState('management'); // 'management', 'bestSellers', 'mostProfitable', 'activityHistory', 'lowStock'
  const [categories, setCategories] = useState(['Bebidas',"Conos", "Maracumangos", "Siropes", ]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formProductData, setFormProductData] = useState(null);
  const [formMode, setFormMode] = useState('create');
  // Solo TI y Administrador/Admin pueden ver/usar Eliminar
  const role = String(currentUser?.role || '').trim();
  const roleKey = role.toLowerCase();
  const { canEdit: canEditModule, canDelete: canDeleteModule } = permOf(currentUser, 'products');
  const canEdit = !!currentUser && !!canEditModule;
  const toggleExportProduct = (id) => {
    if (!canEdit) return;
    setExportSelection((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const openExportReview = () => {
    if (!canEdit || !exportSelection.size || loadingSettings || !fullReady || loadingAll) return;
    setExportError('');
    const sourceName = String(settings.storeName || settings.ticketDisplayName || '').trim();
    if (!sourceName) {
      setExportError('Configura el nombre de esta sede antes de exportar.');
      return;
    }
    try {
      setExportReview(buildProductExport({
        products,
        selectedIds: [...exportSelection],
        sourceName,
        exportId: crypto.randomUUID(),
        exportedAt: new Date().toISOString(),
        catalogComplete: fullReady && !loadingAll,
      }));
    } catch (error) {
      setExportError(`No se pudo analizar la selección: ${error.message}`);
    }
  };
  const canDelete = !!currentUser && !!canDeleteModule && (roleKey === 'ti' || roleKey === 'administrador' || roleKey === 'admin');
// Paginación
const [page, setPage] = useState(1);
const pageSize = 25;
  // 🔎 filtro por tipo de producto
const [productKindFilter, setProductKindFilter] = useState('todos');
const [categoryFilter, setCategoryFilter] = useState('');
const [activityHistory, setActivityHistory] = useState([]);

const normalizedType = (p) => String(p?.type ?? 'final').toLowerCase();
// --- FILTROS (optimizados) ---
// 1) Indexar strings una sola vez
const normalizeSort = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeSearchText = (value = '') => normalizeSort(value);

const indexedProducts = useMemo(
  () => products.map(p => ({
    ...p,
    _n: (p.name || '').toLowerCase(),
    _b: String(p.barcode || '').toLowerCase(),
    _s: normalizeSort(p.name || ''),
    _c: normalizeSort(p.category || ''),
    _searchName: normalizeSearchText(p.name || ''),
  })),
  [products]
);

// 2) Particionar una sola vez por tipo
const { sellables, insumos } = useMemo(() => {
  const s = [], i = [];
  for (const p of indexedProducts) {
    (normalizedType(p) === 'insumo' ? i : s).push(p);
  }
  return { sellables: s, insumos: i };
}, [indexedProducts]);

// 3) Elegir la base según el filtro actual
const baseList = useMemo(() => (
  productKindFilter === 'sellable' ? sellables :
  productKindFilter === 'insumos' ? insumos :
  indexedProducts
), [productKindFilter, sellables, insumos, indexedProducts]);

// 4) Buscar solo sobre ese subset
const visibleProducts = useMemo(() => {
  const term = normalizeSearchText(deferredSearch || '');
  const cat = normalizeSort(categoryFilter || '');
  const filteredByCat = cat
    ? baseList.filter(p => p._c === cat)
    : baseList;
  if (!term) return filteredByCat;
  return filteredByCat.filter(p => p._searchName.includes(term) || p._b.includes(term));
}, [baseList, deferredSearch, categoryFilter]);
// 5) Defer + paginación
const sortedVisibleProducts = useMemo(() => {
  return [...visibleProducts].sort((a, b) =>
    (a._s || '').localeCompare(b._s || '', 'es', { sensitivity: 'base' })
  );
}, [visibleProducts]);
const deferredVisible = useDeferredValue(sortedVisibleProducts);

const uniqueVisible = useMemo(() => {
  const seen = new Set();
  const list = [];
  for (const p of deferredVisible) {
    const key = p.id || p.barcode || p.name;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    list.push(p);
  }
  return list;
}, [deferredVisible]);

const pagedProducts = useMemo(() => {
  const start = (page - 1) * pageSize;
  return uniqueVisible.slice(start, start + pageSize);
}, [uniqueVisible, page, pageSize]);
// Datos para el paginador
const totalPages = Math.max(1, Math.ceil(uniqueVisible.length / pageSize));
const rangeStart = uniqueVisible.length ? (page - 1) * pageSize + 1 : 0;
const rangeEnd   = Math.min(page * pageSize, uniqueVisible.length);


useEffect(() => { setPage(1); }, [deferredSearch, productKindFilter, categoryFilter, products.length]);useEffect(() => {
  const lastPage = Math.max(1, Math.ceil(sortedVisibleProducts.length / pageSize));
  if (page > lastPage) setPage(lastPage);
}, [sortedVisibleProducts.length, page, pageSize]);


// arriba, junto a los useState existentes
const [salesLoaded, setSalesLoaded] = useState(false);
const [salesLoading, setSalesLoading] = useState(false);
const [salesCursor, setSalesCursor] = useState(null);
const [hasMoreSales, setHasMoreSales] = useState(false);
const todayYMD = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const [salesStart, setSalesStart] = useState(() => {
  const d = new Date(Date.now() - 29 * 86400000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
});
const [salesEnd, setSalesEnd] = useState(todayYMD);
const SALES_PAGE = 200;
const SALES_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const formatYmdDisplay = (ymd) => {
  if (!ymd) return '';
  const [year, month, day] = String(ymd).split('-');
  if (!year || !month || !day) return ymd;
  return `${day}/${month}/${year}`;
};

const setSalesRange = (start, end) => {
  setSalesLoaded(false);
  setSalesStart(start);
  setSalesEnd(end);
  setRankPage(1);
};

const applySalesPreset = (preset) => {
  const now = new Date();
  const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  const currentYmd = localNow.toISOString().slice(0, 10);

  if (preset === 'today') {
    setSalesRange(currentYmd, currentYmd);
    return;
  }

  if (preset === 'yesterday') {
    const yesterday = new Date(now.getTime() - 86400000);
    const localYesterday = new Date(yesterday.getTime() - yesterday.getTimezoneOffset() * 60000);
    const ymd = localYesterday.toISOString().slice(0, 10);
    setSalesRange(ymd, ymd);
    return;
  }

  if (preset === 'week') {
    const weekStart = new Date(now);
    const day = weekStart.getDay();
    const diff = day === 0 ? 6 : day - 1;
    weekStart.setDate(weekStart.getDate() - diff);
    const localWeekStart = new Date(weekStart.getTime() - weekStart.getTimezoneOffset() * 60000);
    setSalesRange(localWeekStart.toISOString().slice(0, 10), currentYmd);
    return;
  }

  if (preset === 'month') {
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const localMonthStart = new Date(monthStart.getTime() - monthStart.getTimezoneOffset() * 60000);
    setSalesRange(localMonthStart.toISOString().slice(0, 10), currentYmd);
  }
};

// medir el header fijo para dejar un separador exacto
const headerRef = useRef(null);
const [headerH, setHeaderH] = useState(0);
const formHistoryOpenRef = useRef(false);

useLayoutEffect(() => {
  if (!headerRef.current) return;
  const el = headerRef.current;

  const update = () => setHeaderH(el.offsetHeight);
  update();

  const ro = new ResizeObserver(update);
  ro.observe(el);
  window.addEventListener('resize', update);
  return () => {
    ro.disconnect();
    window.removeEventListener('resize', update);
  };
}, []);



const toDate = (v) => {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const toIsoStringSafe = (value) => {
  const d = toDate(value);
  return d ? d.toISOString() : null;
};

const normalizeSalesDoc = (id, value = {}) => ({
  id,
  date: value.date || null,
  fecha: value.fecha || null,
  updatedAt: toIsoStringSafe(value.updatedAt || value.createdAt || value.date),
  products: Array.isArray(value.products)
    ? value.products.map((p) => ({
        id: p.id,
        quantity: Number(p.quantity || 0),
        price: Number(p.price || 0),
        costPrice: Number(p.costPrice || 0),
      }))
    : [],
});

const mergeSalesDocs = (base = [], incoming = []) => {
  const map = new Map();
  base.forEach((doc) => {
    if (doc?.id) map.set(doc.id, doc);
  });
  incoming.forEach((doc) => {
    if (doc?.id) map.set(doc.id, doc);
  });
  return Array.from(map.values()).sort((a, b) => {
    const ad = toDate(a.date || a.fecha)?.getTime?.() || 0;
    const bd = toDate(b.date || b.fecha)?.getTime?.() || 0;
    return bd - ad;
  });
};

const salesCacheKey = useMemo(
  () => `products:sales:${salesStart}:${salesEnd}`,
  [salesStart, salesEnd]
);

const salesRows = useMemo(() => {
  const start = toDate(`${salesStart}T00:00:00.000Z`);
  const end = toDate(`${salesEnd}T23:59:59.999Z`);
  const rows = [];

  salesDocs.forEach((doc) => {
    const when = toDate(doc.date || doc.fecha);
    if (start && when && when < start) return;
    if (end && when && when > end) return;

    (doc.products || []).forEach((p) => {
      rows.push({
        productId: p.id,
        quantity: Number(p.quantity || 0),
        total: Number(p.price || 0) * Number(p.quantity || 0),
        cost: Number(p.costPrice || 0) * Number(p.quantity || 0),
      });
    });
  });

  return rows;
}, [salesDocs, salesStart, salesEnd]);

const buildSalesQuery = (append = false) => {
  const start = toDate(`${salesStart}T00:00:00.000Z`);
  const end = toDate(`${salesEnd}T23:59:59.999Z`);
  let constraints = [];
  const startIso = start ? start.toISOString() : null;
  const endIso = end ? end.toISOString() : null;
  if (startIso) constraints.push(where('date', '>=', startIso));
  if (endIso) constraints.push(where('date', '<=', endIso));
  constraints.push(orderBy('date', 'desc'), limit(SALES_PAGE));
  if (append && salesCursor) constraints.push(startAfter(salesCursor));
  return query(collection(db, 'ventas'), ...constraints);
};

const fetchSales = async (append = false) => {
  setSalesLoading(true);
  try {
    let cachedDocs = [];
    let lastUpdatedAt = null;

    if (!append && typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(salesCacheKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.ts && Array.isArray(parsed?.docs) && (Date.now() - parsed.ts) < SALES_CACHE_TTL_MS) {
            cachedDocs = parsed.docs;
            lastUpdatedAt = parsed.lastUpdatedAt || null;
            if (cachedDocs.length) {
              setSalesDocs(cachedDocs);
            }
          }
        }
      } catch {}
    }

    const canDelta = !append && lastUpdatedAt;
    const q = canDelta
      ? query(
          collection(db, 'ventas'),
          where('updatedAt', '>', new Date(lastUpdatedAt)),
          orderBy('updatedAt', 'asc'),
          limit(SALES_PAGE)
        )
      : buildSalesQuery(append);

    const snap = await getDocs(q);
    const incomingDocs = snap.docs.map((d) => normalizeSalesDoc(d.id, d.data() || {}));

    let nextDocs;
    if (append) {
      nextDocs = mergeSalesDocs(salesDocs, incomingDocs);
    } else if (canDelta) {
      nextDocs = mergeSalesDocs(cachedDocs, incomingDocs);
    } else {
      nextDocs = incomingDocs;
    }

    setSalesDocs(nextDocs);
    const last = snap.docs[snap.docs.length - 1] || null;
    setSalesCursor(last);
    setHasMoreSales(snap.docs.length === SALES_PAGE);
    setSalesLoaded(true);

    if (!append && typeof window !== 'undefined') {
      try {
        const newestUpdatedAt = nextDocs.reduce((acc, doc) => {
          if (!doc?.updatedAt) return acc;
          if (!acc) return doc.updatedAt;
          return new Date(doc.updatedAt) > new Date(acc) ? doc.updatedAt : acc;
        }, null);

        localStorage.setItem(
          salesCacheKey,
          JSON.stringify({
            ts: Date.now(),
            lastUpdatedAt: newestUpdatedAt,
            docs: nextDocs,
          })
        );
      } catch {}
    }
  } catch (e) {
    console.error('No pude cargar ventas:', e);
  } finally {
    setSalesLoading(false);
  }
};

useEffect(() => {
  const needsSales = currentSubView === 'bestSellers' || currentSubView === 'mostProfitable';
  if (!needsSales) return;
  setSalesCursor(null);
  setHasMoreSales(false);
  fetchSales(false);
}, [currentSubView, salesStart, salesEnd]);

const normalizeUnit = (u = '') => u.toString().trim().toLowerCase();

// Umbral según unidad: unidad => 5 ; ml => 500 ; g => 500
const lowStockThresholdByUnit = (p) => {
  const u = normalizeUnit(p.unit);
  if (u === 'ml' || u === 'mililitros') return 500;
  if (u === 'g' || u === 'gr' || u === 'gramos') return 500;
  return 5; // unidades
};



// Muestra/oculta columnas financieras en la tabla de productos
const SHOW_FINANCIALS = false; // ← déjalo en false para ocultarlas

  // ===== Helpers de costos dinámicos para helados =====
const money = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-CO')}`;

const findByName = (name) => {
  if (!name) return null;
  return products.find(
    x => (x.name || '').trim().toLowerCase() === name.trim().toLowerCase()
  );
};

const avgCostoPorGramoSabores = () => {
  const sabores = products.filter(
    x => x.unit === 'g' && /(helado|sabores)/i.test(x.category || '')
  );
  if (!sabores.length) return 0;
  return sabores.reduce((a, x) => a + Number(x.costPrice || 0), 0) / sabores.length;
};

const estimatedUnitCost = (p) => {
  // Insumo: su costo es el propio costPrice
  if (normalizedType(p) === 'insumo') return Number(p.costPrice || 0);

  // Producto final no-helado: no hay receta dinámica -> usamos costPrice si existe
  if (!p?.config?.isIceCream) return Number(p.costPrice || 0);

  // Helado: costo = base + servilletas + cucharas + (gramos * costo promedio por gramo)
  const sabores = Number(p.config.sabores || 1);
  const gramosPorSabor = Number(p.config.gramosPorSabor || 0);

  const base = p.config.base === 'vaso' ? findByName('vaso') : findByName('conos');
  const serv = findByName('servilletas');
  const cuch = findByName('cucharas');

  const costoBase = Number(base?.costPrice || 0);
  const costoServ = Number(serv?.costPrice || 0) * Number(p.config.servilletas || 0);
  const costoCuch = Number(cuch?.costPrice || 0) * Number(p.config.cucharas || 0);
  const costoExtras = (Array.isArray(p.config.extraItems) ? p.config.extraItems : []).reduce((sum, item) => {
    const insumo = products.find(prod => prod.id === item.productId);
    return sum + (Number(insumo?.costPrice || 0) * Number(item.qty || 0));
  }, 0);

  const costoGramo = avgCostoPorGramoSabores();
  const gramosTotales = sabores * gramosPorSabor;
  const costoHelado = gramosTotales * costoGramo;

  return costoBase + costoServ + costoCuch + costoExtras + costoHelado;
};

// ── Helpers para crear recetas programáticamente ───────────────────────────────
const normalize = (s='') =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// Busca un producto por cualquiera de estos nombres candidatos (ignora acentos y plural)
const findOneByNameAny = (candidates=[]) => {
  return products.find(p => {
    const n = normalize(p?.name || '');
    return candidates.some(c => n === normalize(c));
  });
};

// Devuelve { productId, qty, unit } para baseItems a partir de nombres
const resolveBaseItems = (baseItemsByName) => {
  const missing = [];
  const resolved = baseItemsByName.map(b => {
    const found =
      findOneByNameAny(Array.isArray(b.name) ? b.name : [b.name]);
    if (!found) {
      missing.push(Array.isArray(b.name) ? b.name.join(' / ') : b.name);
      return null;
    }
    return {
      productId: found.id,
      qty: Number(b.qty || 0),
      unit: b.unit || 'unidad',
    };
  });

  if (missing.length) {
    alert(`Faltan insumos en tu catálogo: ${missing.join(', ')}`);
    return null;
  }
  return resolved;
};

// Crea un producto tipo receta en Firestore
const createRecipeProduct = async ({
  name,
  price,
  category = 'receta',
  barcode,
  baseItemsByName = [],
  choiceGroups = []
}) => {
  // 1) Resolver IDs de insumos base
  const baseItems = resolveBaseItems(baseItemsByName);
  if (!baseItems) return; // faltan insumos

  // 2) Armar documento
  const recipeDoc = {
    name,
    barcode: barcode || `RC-${Date.now()}`,
    image: '',
    price: Number(price) || 0,
    type: 'receta',
    trackStock: false,
    category,
    config: { recipe: { baseItems, choiceGroups } },
    registeredBy: currentUser || '',
    registrationDate: new Date().toISOString().slice(0,10),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // 3) Guardar (✅ solo una vez)
  const ref = await addDoc(collection(db, 'productos'), recipeDoc);
  alert(`Receta "${name}" creada`);
};

// ── Recetas rápidas: Cono 1 y 2 sabores ───────────────────────────────────────
const createCono1Sabor = async () => {
  await createRecipeProduct({
    name: 'Cono 1 sabor',
    price: 5500,                       // ajusta si tu precio es otro
    category: 'Conos',
    baseItemsByName: [
      { name: ['Cono', 'Conos'], qty: 1, unit: 'unidad' },
      { name: ['Servilleta', 'Servilletas'], qty: 2, unit: 'unidad' },
      { name: ['Cuchara', 'Cucharas'], qty: 1, unit: 'unidad' },
    ],
    choiceGroups: [
      {
        id: 'sabores',
        label: 'Sabores',
        min: 1,
        max: 1,
        perPickQty: 90,                // 90 g por sabor
        unit: 'g',
        fromCategory: 'helados'
      }
    ]
  });
};

const createCono2Sabores = async () => {
  await createRecipeProduct({
    name: 'Cono 2 sabores',
    price: 6500,                       // pon aquí tu precio real
    category: 'Conos',
    baseItemsByName: [
      { name: ['Cono', 'Conos'], qty: 1, unit: 'unidad' },
      { name: ['Servilleta', 'Servilletas'], qty: 2, unit: 'unidad' },
      { name: ['Cuchara', 'Cucharas'], qty: 1, unit: 'unidad' },
    ],
    choiceGroups: [
      {
        id: 'sabores',
        label: 'Sabores',
        min: 2,
        max: 2,
        perPickQty: 90,
        unit: 'g',
        fromCategory: 'helados'
      }
    ]
  });
};




useEffect(() => {
  const fromProducts = Array.from(new Set(
    (products || [])
      .map(p => String(p?.category || '').trim())
      .filter(Boolean)
  ));
  setCategories(prev => Array.from(new Set([...(prev || []), ...fromProducts])));
}, [products]);

useEffect(() => {
  loadAllProductsOnce();
}, [loadAllProductsOnce]);

const categoryOptions = useMemo(() => {
  const unique = Array.from(new Set((categories || []).map(c => String(c || '').trim()).filter(Boolean)));
  return unique.sort((a, b) =>
    normalizeSort(a).localeCompare(normalizeSort(b), 'es', { sensitivity: 'base' })
  );
}, [categories]);

// Al entrar al módulo, fuerza scroll al tope
useEffect(() => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
}, []);

useEffect(() => {
  if (!showForm || typeof window === 'undefined') return undefined;

  if (!formHistoryOpenRef.current) {
    window.history.pushState({ ...(window.history.state || {}), productFormModal: true }, '');
    formHistoryOpenRef.current = true;
  }

  const handlePopState = () => {
    if (!formHistoryOpenRef.current) return;
    formHistoryOpenRef.current = false;
    setShowForm(false);
    setEditingProduct(null);
  };

  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}, [showForm]);

const openProductForm = (product = null) => {
  setEditingProduct(product);
  setFormProductData(product);
  setFormMode(product ? 'edit' : 'create');
  setShowForm(true);
};

const closeProductForm = () => {
  setShowForm(false);
  setEditingProduct(null);
  setFormProductData(null);
  setFormMode('create');

  if (typeof window !== 'undefined' && formHistoryOpenRef.current) {
    formHistoryOpenRef.current = false;
  }
};

const buildClonedProductDraft = (product) => {
  if (!product) return null;
  const isInsumo = normalizedType(product) === 'insumo';

  return {
    ...product,
    name: '',
    barcode: '',
    image: '',
    stock: isInsumo ? 0 : Number(product.stock || 0),
    registeredBy: safeUser(currentUser),
    registrationDate: new Date().toISOString().split('T')[0],
  };
};

const handleCloneProduct = (product) => {
  const draft = buildClonedProductDraft(product);
  if (!draft) return;
  setEditingProduct(null);
  setFormProductData(draft);
  setFormMode('clone');
  setShowForm(true);
};


const asegurarCategoria = (categoria) => {
  const cat = String(
    typeof categoria === 'string' ? categoria : (categoria?.value ?? categoria ?? '')
  ).trim();

  if (!cat) return;

  setCategories(prev => (prev.includes(cat) ? prev : [...prev, cat]));
};
const actualizarProducto = async (id, productoFormateado) => {
  const ref = doc(db, "productos", id);
  await updateDoc(ref, { ...productoFormateado, updatedAt: serverTimestamp() });
};

const agregarProductoOld = async (productoFormateado) => {
  await addDoc(collection(db, "productos"), productoFormateado);
};

const agregarProducto = async (productoFormateado) => {
  const docRef = await addDoc(collection(db, "productos"), productoFormateado);
  return { id: docRef.id };
};

// Nuevos helpers con marcas de tiempo para cache incremental
const actualizarProductoConMarca = async (id, productoFormateado) => {
  const ref = doc(db, "productos", id);
  await updateDoc(ref, { ...productoFormateado, updatedAt: serverTimestamp() });
};

const crearProductoConMarca = async (productoFormateado) => {
  const now = serverTimestamp();
  const docRef = await addDoc(collection(db, "productos"), { ...productoFormateado, createdAt: now, updatedAt: now });
  return { id: docRef.id };
};

const handleSaveProduct = async (newProduct) => {
  try {
    let categoriaSegura = '';

    // Detectar si viene como objeto o string
    if (typeof newProduct.category === 'object' && newProduct.category?.value) {
      categoriaSegura = newProduct.category.value;
    } else {
      categoriaSegura = newProduct.category;
    }

const productoFormateado = {
  name: newProduct.name || '',
  barcode: newProduct.barcode || '',
  image: newProduct.image || '',
  price: (newProduct.type === 'insumo') ? 0 : Number(newProduct.price) || 0,
  costPrice: Number(newProduct.costPrice) || 0,
  stock: Number(newProduct.stock) || 0,
  category: categoriaSegura,
  type: newProduct.type || 'final',
  unit: newProduct.type === 'insumo' ? (newProduct.unit || 'unidad') : '',
  config: newProduct.config || null,   // lo usaremos para “Cono 2 sabores”, etc.
  yieldConfig: newProduct.type === 'insumo' ? (newProduct.yieldConfig || null) : null,
  excludeFromEntries: newProduct.type !== 'insumo' && !!newProduct.excludeFromEntries,
  registeredBy: newProduct.registeredBy || '',
  registrationDate: newProduct.registrationDate || new Date().toISOString().split('T')[0]
};

  const clientUpdatedAt = Date.now();
  if (editingProduct) {
    await actualizarProductoConMarca(editingProduct.id, productoFormateado);


    try {
      await logAudit({
        module: 'Productos',
        action: 'product.updated',
        severity: 'info',
        target: { id: editingProduct.id },
        details: { name: productoFormateado?.name, category: productoFormateado?.category }
      }, currentUser);
    } catch {}

  setProducts(prev => {
    return prev.map(p =>
      p.id === editingProduct.id
        ? { id: editingProduct.id, ...productoFormateado, updatedAt: clientUpdatedAt }
        : p
    );
  });
  } else {
    const { id: newId } = await crearProductoConMarca(productoFormateado);
    try {
      await logAudit({
        module: 'Productos',
        action: 'product.created',
        severity: 'info',
        details: { name: productoFormateado?.name, category: productoFormateado?.category }
      }, currentUser);
    } catch {}
    setProducts(prev => [...prev, sanitizeProduct({ id: newId, ...productoFormateado, createdAt: clientUpdatedAt, updatedAt: clientUpdatedAt })]);
  }

categoriaSegura = String(categoriaSegura).trim();
asegurarCategoria(categoriaSegura);

    closeProductForm();
  } catch (error) {
    console.error("❌ Error al guardar producto:", error);
    alert("Hubo un problema al guardar el producto.");
  }
};




// Modal mejorado de confirmación de eliminación
const emptyDeleteConfirm = {
  open: false,
  product: null,
  loading: false,
  checking: false,
  dependencies: [],
  dependencyCheckFailed: false,
  error: '',
};
const [deleteConfirm, setDeleteConfirm] = useState(emptyDeleteConfirm);

const checkRecipeDependencies = async (productId) => {
  const snapshot = await getDocs(collection(db, 'productos'));
  const allProducts = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
  return findProductRecipeDependencies(allProducts, productId);
};

const openDeleteModal = async (product) => {
  if (!canDelete) return; // guard extra por seguridad
  setDeleteConfirm({ ...emptyDeleteConfirm, open: true, product, checking: true });
  try {
    const dependencies = await checkRecipeDependencies(product.id);
    setDeleteConfirm(prev => (
      prev.product?.id === product.id
        ? { ...prev, checking: false, dependencies }
        : prev
    ));
  } catch (error) {
    console.error('Error verificando dependencias de recetas:', error);
    setDeleteConfirm(prev => (
      prev.product?.id === product.id
        ? {
          ...prev,
          checking: false,
          dependencyCheckFailed: true,
          error: 'No se pudieron verificar las recetas. Por seguridad, la eliminación está bloqueada.',
        }
        : prev
    ));
  }
};

const closeDeleteModal = () => {
  if (deleteConfirm.loading) return;
  setDeleteConfirm(emptyDeleteConfirm);
};

const confirmDelete = async () => {
  if (!canDelete) {
    setDeleteConfirm(prev => ({ ...prev, error: 'No tienes permisos para eliminar.' }));
    return;
  }
  const prod = deleteConfirm.product;
  if (!prod?.id) return;
  try {
    setDeleteConfirm(prev => ({ ...prev, loading: true, error: '' }));
    const dependencies = await checkRecipeDependencies(prod.id);
    if (dependencies.length > 0) {
      setDeleteConfirm(prev => ({
        ...prev,
        loading: false,
        dependencies,
        error: 'No se puede eliminar: este insumo todavía forma parte de recetas.',
      }));
      return;
    }
    await updateDoc(doc(db, 'productos', prod.id), {
      deleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deletedBy: currentUser?.uid || currentUser?.email || currentUser?.username || '',
    });
    setProducts(prev => prev.filter(p => p.id !== prod.id));
    setDeleteConfirm(emptyDeleteConfirm);
    try {
      await logAudit({
        module: 'Productos',
        action: 'product.deleted',
        severity: 'critical',
        target: { id: prod.id },
        details: { name: prod?.name, category: prod?.category }
      }, currentUser);
    } catch {}
  } catch (error) {
    console.error('Error al eliminar en Firestore:', error);
    setDeleteConfirm(prev => ({ ...prev, loading: false, error: 'No se pudo eliminar el producto. Intenta de nuevo.' }));
  }
};

  const handleEditProduct = (product) => {
    openProductForm(product);
  };

const productIndex = useMemo(() => {
  const m = new Map();
  products.forEach(p => m.set(p.id, p));
  return m;
}, [products]);

const bestSellers = useMemo(() => {
  const statsById = new Map();
  salesRows.forEach((s) => {
    const prev = statsById.get(s.productId) || { quantity: 0, totalSales: 0 };
    statsById.set(s.productId, {
      quantity: prev.quantity + s.quantity,
      totalSales: prev.totalSales + s.total,
    });
  });
  return Array.from(statsById.entries())
    .map(([id, stats]) => {
      const p = productIndex.get(id);
      return p
        ? {
            ...p,
            totalQuantitySold: stats.quantity,
            totalSalesAmount: stats.totalSales,
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.totalQuantitySold - a.totalQuantitySold);
}, [salesRows, productIndex]);

const mostProfitable = useMemo(() => {
  const profitById = new Map();
  salesRows.forEach(s => profitById.set(s.productId, (profitById.get(s.productId) || 0) + (s.total - s.cost)));
  return Array.from(profitById.entries())
    .map(([id, profit]) => {
      const p = productIndex.get(id);
      return p ? { ...p, totalProfit: profit } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.totalProfit - a.totalProfit);
}, [salesRows, productIndex]);

// Paginación para rankings
const [rankPage, setRankPage] = useState(1);
const rankPageSize = 10;
const bestSellersTotalPages = Math.max(1, Math.ceil(bestSellers.length / rankPageSize));
const mostProfitableTotalPages = Math.max(1, Math.ceil(mostProfitable.length / rankPageSize));
const pagedBestSellers = useMemo(() => {
  const start = (rankPage - 1) * rankPageSize;
  return bestSellers.slice(start, start + rankPageSize);
}, [bestSellers, rankPage]);
const pagedMostProfitable = useMemo(() => {
  const start = (rankPage - 1) * rankPageSize;
  return mostProfitable.slice(start, start + rankPageSize);
}, [mostProfitable, rankPage]);

const [lowStockPage, setLowStockPage] = useState(1);
const lowStockPageSize = 20;

useEffect(() => {
  setRankPage(1);
}, [salesStart, salesEnd, currentSubView]);

useEffect(() => {
  const currentTotalPages = currentSubView === 'mostProfitable'
    ? mostProfitableTotalPages
    : bestSellersTotalPages;
  if (rankPage > currentTotalPages) setRankPage(currentTotalPages);
}, [rankPage, currentSubView, bestSellersTotalPages, mostProfitableTotalPages]);

const lowStockProducts = useMemo(() => {
  const seen = new Set();
  const list = [];
  products.forEach(p => {
    const esReceta = hasRecipeConfiguration(p);
    if (esReceta) return false;

    const stock = Number(p.stock || 0);
    const thr   = lowStockThresholdByUnit(p);
    const u     = normalizeUnit(p.unit);
    const isMl  = (u === 'ml' || u === 'mililitros');
    const isG   = (u === 'g'  || u === 'gr' || u === 'gramos');

    const isLow = (isMl || isG) ? stock < thr : stock <= thr;
    if (!isLow) return;

    const key = p.id || p.barcode || p.name;
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    list.push(p);
  });
  return list;
}, [products]);

const lowStockTotalPages = Math.max(1, Math.ceil(lowStockProducts.length / lowStockPageSize));
const pagedLowStockProducts = useMemo(() => {
  const start = (lowStockPage - 1) * lowStockPageSize;
  return lowStockProducts.slice(start, start + lowStockPageSize);
}, [lowStockPage, lowStockProducts]);

useEffect(() => {
  if (currentSubView === 'lowStock') setLowStockPage(1);
}, [currentSubView]);

useEffect(() => {
  if (lowStockPage > lowStockTotalPages) setLowStockPage(lowStockTotalPages);
}, [lowStockPage, lowStockTotalPages]);

  const renderSubView = () => {
    switch (currentSubView) {
case 'management':
  return (
    <>
      {canEdit && !showForm && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-sky-200 bg-white p-3">
          <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" aria-label="Seleccionar productos de esta página" className="h-5 w-5" disabled={!pagedProducts.length}
              checked={pagedProducts.length > 0 && pagedProducts.every((product) => exportSelection.has(product.id))}
              ref={(input) => { if (input) input.indeterminate = pagedProducts.some((p) => exportSelection.has(p.id)) && !pagedProducts.every((p) => exportSelection.has(p.id)); }}
              onChange={(event) => {
                const checked = event.target.checked;
                setExportSelection((previous) => {
                  const next = new Set(previous);
                  pagedProducts.forEach((product) => { if (checked) next.add(product.id); else next.delete(product.id); });
                  return next;
                });
              }} />
            Seleccionar página ({pagedProducts.length})
          </label>
          <span role="status" className="text-sm font-semibold">{exportSelection.size} productos seleccionados</span>
          {exportSelection.size > 0 && <>
            <button type="button" onClick={() => setExportSelection(new Set())} className="min-h-[44px] rounded-lg border border-slate-300 px-3 text-sm">Quitar selección</button>
            <button type="button" onClick={openExportReview} disabled={!fullReady || loadingAll || loadingSettings} className="min-h-[44px] rounded-lg bg-sky-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Exportar seleccionados</button>
          </>}
          {(!fullReady || loadingAll) && <p className="w-full text-sm text-slate-600">Es necesario completar la carga del catálogo para analizar todas las dependencias.</p>}
          {loadingSettings && <p className="w-full text-sm text-slate-600">Espera un momento: estamos cargando el nombre de esta sede para identificar correctamente el archivo.</p>}
          {exportError && <p role="alert" className="w-full text-sm text-red-700">{exportError}</p>}
        </div>
      )}
      <div className="mb-5 rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-sm sm:p-5">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(280px,1fr)] md:items-end">
          <div className="space-y-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Catalogo</p>
              <h2 className="text-lg font-bold text-slate-900 sm:text-xl">Encuentra y filtra productos rapido</h2>
            </div>
            <div className="relative">
              <svg className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar por nombre o codigo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-800 shadow-inner outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-slate-900 px-4 py-3 text-white">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-300">Visibles</p>
              <p className="mt-1 text-2xl font-black">{uniqueVisible.length}</p>
            </div>
            <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-emerald-900">
              <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-600">Pagina</p>
              <p className="mt-1 text-2xl font-black">{page}</p>
            </div>
            <div className="col-span-2 rounded-2xl bg-sky-50 px-4 py-3 text-sky-900 sm:col-span-1">
              <p className="text-[11px] uppercase tracking-[0.2em] text-sky-700">Categoria</p>
              <p className="mt-1 truncate text-sm font-bold">{categoryFilter || 'Todas'}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { v: 'sellable', t: 'Para vender' },
            { v: 'insumos', t: 'Insumos' },
            { v: 'todos', t: 'Todos' },
          ].map((btn) => (
            <button
              key={btn.v}
              onClick={() => startTransition(() => setProductKindFilter(btn.v))}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                productKindFilter === btn.v
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              } ${isPending ? 'opacity-80' : ''}`}
              aria-busy={isPending}
            >
              {btn.t}
            </button>
          ))}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-[auto_minmax(0,240px)] sm:items-center sm:justify-between">
          <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Mostrando <span className="font-bold text-slate-900">{rangeStart}</span> a <span className="font-bold text-slate-900">{rangeEnd}</span> de <span className="font-bold text-slate-900">{uniqueVisible.length}</span> productos.
          </div>
          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2">
            <label className="shrink-0 text-sm font-medium text-slate-600">Categoria</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            >
              <option value="">Todas</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="mb-6 hidden">
        <input
          type="text"
          placeholder="Buscar productos por nombre o código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/2 px-5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 shadow-sm"
        />
      </div>

      {/* Filtro: Para vender / Insumos / Todos */}
      <div className="mb-4 hidden flex-wrap items-center gap-2">
        {[
          { v: 'sellable', t: 'Para vender' },
          { v: 'insumos',  t: 'Insumos' },
          { v: 'todos',    t: 'Todos' },
        ].map(btn => (
          <button
            key={btn.v}
            onClick={() => startTransition(() => setProductKindFilter(btn.v))}
            className={`px-3 py-1 rounded-full text-sm ${
              productKindFilter === btn.v
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            } ${isPending ? 'opacity-80' : ''}`}
            aria-busy={isPending}
          >
            {btn.t}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-1">
          <label className="text-sm text-gray-600">Categoria</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1 rounded-lg border border-gray-300 bg-white text-sm"
          >
            <option value="">Todas</option>
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {showForm ? (
        <ProductForm
          onSave={handleSaveProduct}
          onCancel={closeProductForm}
          currentUser={currentUser}
          categories={categories}
          initialProductData={formProductData}
          mode={formMode}
          products={products}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden w-full">
          <div className="overflow-x-auto w-full">
            {productsLoading && products.length === 0 ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div>
                {pagedProducts.length === 0 && (
                  <div className="px-5 py-12 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                      <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                      </svg>
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-slate-900">No hay productos para mostrar</h3>
                    <p className="mt-2 text-sm text-slate-500">Ajusta la busqueda o los filtros para encontrar mas resultados.</p>
                  </div>
                )}
                    <div className="grid gap-3 p-3 sm:grid-cols-2 xl:hidden">
                      {pagedProducts.map((product) => {
                         const esReceta = hasRecipeConfiguration(product);
                         const esPromocion = isPromotionProduct(product);
                        const stockReal = Number(product.stock || 0);
                        const u = (product.unit || '').toString().trim().toLowerCase();
                        const thr = (u === 'ml' || u === 'mililitros') ? 500
                                  : (u === 'g' || u === 'gr' || u === 'gramos') ? 500
                                  : 5;
                        const isMlOrG = (u === 'ml' || u === 'mililitros' || u === 'g' || u === 'gr' || u === 'gramos');
                        const isLow = isMlOrG ? stockReal < thr : stockReal <= thr;
                        const suf = (u === 'ml' || u === 'mililitros') ? ' ml'
                                  : (u === 'g' || u === 'gr' || u === 'gramos') ? ' g'
                                  : '';

                        return (
                          <article key={`mobile-${product.id}`} className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm">
                            {canEdit && <label className="mb-3 flex min-h-[44px] items-center gap-2 text-xs font-semibold text-sky-800"><input type="checkbox" aria-label={`Seleccionar ${product.name}`} className="h-5 w-5" checked={exportSelection.has(product.id)} onChange={() => toggleExportProduct(product.id)} />Seleccionar para exportar</label>}
                            <div className="flex items-start gap-3">
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  loading="lazy"
                                  width={64}
                                  height={64}
                                  className="h-16 w-16 rounded-2xl object-cover border border-slate-200 bg-white shadow-sm"
                                />
                              ) : (
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-100 text-[11px] font-medium text-slate-400">
                                  Sin foto
                                </div>
                              )}

                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <h3 className="truncate text-base font-black uppercase tracking-tight text-slate-900">{product.name}</h3>
                                    <p className="mt-1 text-xs text-slate-500">Codigo: {product.barcode || 'Sin codigo'}</p>
                                  </div>
                                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${normalizedType(product) === 'insumo' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {normalizedType(product) === 'insumo' ? 'Insumo' : 'Venta'}
                                  </span>
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-2">
                                  <div className="rounded-2xl bg-slate-100 px-3 py-2">
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Precio</p>
                                    <p className="mt-1 text-sm font-bold text-slate-900">
                                      {normalizedType(product) === 'insumo' ? 'No aplica' : money(product.price || 0)}
                                    </p>
                                  </div>
                                  {SHOW_FINANCIALS && (
                                    <div className="rounded-2xl bg-slate-100 px-3 py-2">
                                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Costo</p>
                                      <p className="mt-1 text-sm font-bold text-slate-900">
                                        {money(normalizedType(product) === 'insumo'
                                          ? Number(product.costPrice || 0)
                                          : estimatedUnitCost(product))}
                                      </p>
                                    </div>
                                  )}
                                  <div className="col-span-2 rounded-2xl bg-slate-100 px-3 py-2">
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Stock</p>
                                    <p className={`mt-1 text-sm font-bold ${isLow ? 'text-red-600' : 'text-emerald-600'}`}>
                                      {esPromocion ? 'Por receta' : esReceta ? 'No maneja' : `${stockReal}${suf}`}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Categoria</p>
                                <p className="mt-1 truncate font-semibold text-slate-800">{product.category || 'Sin categoria'}</p>
                              </div>
                              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Registro</p>
                                <p className="mt-1 truncate font-semibold text-slate-800">{product.registrationDate || 'Sin fecha'}</p>
                              </div>
                            </div>

                            <div className="mt-3 rounded-2xl bg-slate-900 px-3 py-2 text-xs text-slate-200">
                              <span className="font-semibold text-white">{safeUser(product.registeredBy) || 'Sin usuario'}</span>
                            </div>

                            {(canEdit || canDelete) && (
                              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {canEdit && (
                                  <button
                                    onClick={() => handleEditProduct(product)}
                                    className="rounded-2xl bg-sky-600 px-3 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
                                  >
                                    Editar
                                  </button>
                                )}
                                {canEdit && (
                                  <button
                                    onClick={() => handleCloneProduct(product)}
                                    className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                                  >
                                    Clonar
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => openDeleteModal(product)}
                                    className="rounded-2xl border border-red-200 bg-red-50 px-3 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                                  >
                                    Eliminar
                                  </button>
                                )}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>

                    <div className="hidden xl:block">
                      <table className="w-full min-w-[1180px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {canEdit && <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500">Exportar</th>}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imagen</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Venta</th>
                      {SHOW_FINANCIALS && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo Unitario</th>
                      )}
                      {SHOW_FINANCIALS && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilidad</th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registrado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {pagedProducts.map((product) => {
                      const unitCost = normalizedType(product) === 'insumo'
                        ? Number(product.costPrice || 0)
                        : estimatedUnitCost(product);

                      const esReceta = hasRecipeConfiguration(product);
                      const esPromocion = isPromotionProduct(product);
                      const stockReal = Number(product.stock || 0);
                      const u = (product.unit || '').toString().trim().toLowerCase();
                      const thr = (u === 'ml' || u === 'mililitros') ? 500
                                : (u === 'g'  || u === 'gr' || u === 'gramos') ? 500
                                : 5;
                      const isMlOrG = (u === 'ml' || u === 'mililitros' || u === 'g' || u === 'gr' || u === 'gramos');
                      const isLow = isMlOrG ? stockReal < thr : stockReal <= thr;
                      const suf = (u === 'ml' || u === 'mililitros') ? ' ml'
                                : (u === 'g'  || u === 'gr' || u === 'gramos') ? ' g'
                                : '';

                      return (
                        <tr key={product.id} className="hover:bg-gray-50 transition duration-150">
                          {canEdit && <td className="px-3 py-4"><input type="checkbox" aria-label={`Seleccionar ${product.name}`} className="h-5 w-5" checked={exportSelection.has(product.id)} onChange={() => toggleExportProduct(product.id)} /></td>}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                loading="lazy"
                                width={48}
                                height={48}
                                className="h-12 w-12 object-cover rounded-md border border-gray-200 bg-white"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-md border border-gray-200 bg-gray-50 text-[11px] text-gray-400 flex items-center justify-center">
                                Sin foto
                              </div>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {product.name}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {product.barcode}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {normalizedType(product) === 'insumo' ? '—' : money(product.price || 0)}
                          </td>

                          {SHOW_FINANCIALS && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {normalizedType(product) === 'insumo'
                                ? money(unitCost)
                                : `≈ ${money(unitCost)}`}
                            </td>
                          )}

                          {SHOW_FINANCIALS && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {normalizedType(product) === 'insumo'
                                ? '—'
                                : money((product.price || 0) - (unitCost || 0))}
                            </td>
                          )}

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {product.category}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                            {esPromocion ? 'Por receta' : esReceta ? '—' : (
                              <span className={isLow ? 'text-red-600' : 'text-green-600'}>
                                {stockReal}{suf}
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex flex-col">
<span>{safeUser(product.registeredBy) || '—'}</span>
                              <span className="text-xs text-gray-400">{product.registrationDate}</span>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-3">
                              {canEdit && (
                                <button
                                  onClick={() => handleEditProduct(product)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Editar
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  onClick={() => handleCloneProduct(product)}
                                  className="text-emerald-600 hover:text-emerald-800"
                                >
                                  Clonar
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => openDeleteModal(product)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Eliminar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                    </div>

                {/* Paginador */}
                <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50/80 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600">
                    Mostrando <span className="font-semibold">{rangeStart}</span>
                    –<span className="font-semibold">{rangeEnd}</span> de <span className="font-semibold">{visibleProducts.length}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                    <button
                      type="button"
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
                    >
                      « Primero
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
                    >
                      ‹ Anterior
                    </button>
<span className="col-span-2 rounded-xl bg-slate-900 px-3 py-2 text-center text-sm font-medium text-white sm:col-span-1 sm:bg-transparent sm:px-1 sm:py-0 sm:text-slate-700">
  Página <span className="font-semibold">{page}</span> de <span className="font-semibold">{totalPages}</span>
</span>

<button
  type="button"
  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
  disabled={page === totalPages}
  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
>
  Siguiente ›
</button>

<button
  type="button"
  onClick={() => setPage(totalPages)}
  disabled={page === totalPages}
  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
>
  Último »
</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
      case 'bestSellers':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Productos Más Vendidos</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad Vendida</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Vendido</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedBestSellers.length > 0 ? (
                    pagedBestSellers.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.totalQuantitySold}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{money(product.totalSalesAmount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                        No hay datos de ventas para mostrar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {bestSellers.length > 0 && (
                <div className="mt-4 flex items-center justify-between gap-3">
                  <button
                    onClick={() => setRankPage((p) => Math.max(1, p - 1))}
                    disabled={rankPage === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {rankPage} de {bestSellersTotalPages}
                  </span>
                  <button
                    onClick={() => setRankPage((p) => Math.min(bestSellersTotalPages, p + 1))}
                    disabled={rankPage === bestSellersTotalPages}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
              {hasMoreSales && (
                <div className="mt-3 flex justify-center">
                  <button
                    onClick={() => fetchSales(true)}
                    className="px-4 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Cargar más ventas (otra página)
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case 'mostProfitable':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Productos Más Rentables</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ganancia Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedMostProfitable.length > 0 ? (
                    pagedMostProfitable.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{money(product.totalProfit)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="px-6 py-4 text-center text-sm text-gray-500">
                        No hay datos de ganancias para mostrar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {mostProfitable.length > 0 && (
                <div className="mt-4 flex items-center justify-between gap-3">
                  <button
                    onClick={() => setRankPage((p) => Math.max(1, p - 1))}
                    disabled={rankPage === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {rankPage} de {mostProfitableTotalPages}
                  </span>
                  <button
                    onClick={() => setRankPage((p) => Math.min(mostProfitableTotalPages, p + 1))}
                    disabled={rankPage === mostProfitableTotalPages}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
              {hasMoreSales && (
                <div className="mt-3 flex justify-center">
                  <button
                    onClick={() => fetchSales(true)}
                    className="px-4 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Cargar más ventas (otra página)
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case 'lowStock':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
<h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
  Productos con Stock Bajo (≤ 5 und | &lt; 500 ml | &lt; 500 g)
</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
<thead className="bg-gray-50">
  <tr>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Actual</th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
  </tr>
</thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedLowStockProducts.length > 0 ? (
                    pagedLowStockProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.barcode}</td>
<td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
  {(() => {
    const u = normalizeUnit(product.unit);
    const thr = lowStockThresholdByUnit(product);
    const stock = Number(product.stock || 0);
    const isMlOrG = (u === 'ml' || u === 'mililitros' || u === 'g' || u === 'gr' || u === 'gramos');
    const isLow = isMlOrG ? stock < thr : stock <= thr; // ml/g < 500 ; unidades ≤ 5
    const clase = isLow ? 'text-red-600' : 'text-green-600';
    const suf = (u === 'ml' || u === 'mililitros') ? ' ml'
              : (u === 'g'  || u === 'gr' || u === 'gramos') ? ' g'
              : '';
    return <span className={clase}>{stock}{suf}</span>;
  })()}
</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                        ¡Excelente! No hay productos con stock bajo.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {lowStockProducts.length > 0 && (
                <div className="mt-4 flex items-center justify-between gap-3">
                  <button
                    onClick={() => setLowStockPage((p) => Math.max(1, p - 1))}
                    disabled={lowStockPage === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {lowStockPage} de {lowStockTotalPages}
                  </span>
                  <button
                    onClick={() => setLowStockPage((p) => Math.min(lowStockTotalPages, p + 1))}
                    disabled={lowStockPage === lowStockTotalPages}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case 'activityHistory':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Historial de Actividad de Productos</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalles</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activityHistory.length > 0 ? (
                    activityHistory.map((activity) => (
                      <tr key={activity.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{activity.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{activity.user}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{activity.action}</td>
                        <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">{activity.details}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                        No hay actividad registrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const openImportPicker = () => {
    if (!canEdit || importingProducts) return;
    setTransferImportError('');
    importInputRef.current?.click();
  };

  const handleImportProducts = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!canEdit) {
      setTransferImportError('Ya no tienes permiso de edición de Productos para revisar transferencias.');
      return;
    }

    setImportingProducts(true);
    setTransferImportAnalysis(null);
    setTransferImportError('');
    try {
      const inspected = await inspectProductTransferFile(file);
      if (!fullReady || loadingAll) {
        setTransferImportError('Estamos cargando el catálogo completo para comparar esta transferencia. Cuando termine la carga, selecciona el mismo archivo nuevamente.');
        await loadAllProductsOnce();
        return;
      }
      setTransferImportAnalysis(analyzeProductTransfer(inspected.value, products));
    } catch (error) {
      setTransferImportError(error?.message || 'No se pudo leer este archivo de transferencia.');
    } finally {
      setImportingProducts(false);
    }
  };

return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
    {/* HEADER fijo pero compacto y responsive */}
    <div
      ref={headerRef}
      className="sticky top-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200"
    >
      <div className="mx-auto flex max-w-screen-2xl flex-col gap-3 px-3 py-3 sm:px-6 lg:max-w-[1700px] lg:px-10 xl:flex-row xl:items-center xl:justify-between">
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <button
            onClick={onBack}
            className="flex min-w-0 items-center justify-center gap-2 rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-600 transition hover:border-blue-400 hover:text-blue-800 sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver al Menú
          </button>

          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportProducts}
          />

          <button
            type="button"
            onClick={openImportPicker}
            disabled={!canEdit || importingProducts}
            className="flex min-w-0 items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 sm:px-5 sm:text-base"
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {importingProducts ? 'Preparando...' : 'Importar productos'}
          </button>

          <button
            onClick={() => { openProductForm(); setCurrentSubView('management'); }}
            className="col-span-2 flex min-w-0 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white shadow-sm transition hover:bg-emerald-700 sm:col-span-1 sm:px-5 sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Producto
          </button>
        </div>

        <h1 className="text-center text-2xl font-extrabold leading-tight text-gray-900 sm:text-3xl xl:text-left xl:text-4xl">
          Gestión de Productos
        </h1>
      </div>
    </div>

    {exportReview && <ProductExportDialog review={exportReview} canEdit={canEdit} onClose={() => setExportReview(null)} />}
    {transferImportAnalysis && <ProductImportWizard analysis={transferImportAnalysis} canEdit={canEdit} onClose={() => setTransferImportAnalysis(null)} onImported={async () => { await loadAllProductsOnce({ force: true }); setExportSelection(new Set()); }} />}

    {/* Contenido principal */}
    <div className="mx-auto w-full max-w-screen-2xl px-3 pb-10 sm:px-5 lg:max-w-[1700px] lg:px-8">
      {transferImportError && <div role="alert" className="mt-4 whitespace-pre-line rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">{transferImportError}</div>}

      {/* Botonera de sub-vistas */}
      <div className="py-4">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { key: 'management', label: 'Gestión de Productos' },
            { key: 'bestSellers', label: 'Más Vendidos' },
            { key: 'mostProfitable', label: 'Más Rentables' },
            { key: 'lowStock', label: 'Stock Bajo' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => startTransition(() => setCurrentSubView(tab.key))}
              className={`px-4 py-2 rounded-lg text-sm sm:text-base font-semibold whitespace-nowrap transition duration-200 ${
                currentSubView === tab.key
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {(currentSubView === 'bestSellers' || currentSubView === 'mostProfitable') && (
          <div className="mt-3 bg-white border rounded-lg p-3 shadow-sm">
            <div className="mb-3 flex flex-wrap gap-2">
              {[
                { key: 'today', label: 'Hoy' },
                { key: 'yesterday', label: 'Ayer' },
                { key: 'week', label: 'Esta semana' },
                { key: 'month', label: 'Este mes' },
              ].map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applySalesPreset(preset.key)}
                  className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
              <input
                type="date"
                value={salesStart}
                onChange={(e) => setSalesRange(e.target.value, salesEnd)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
              <input
                type="date"
                value={salesEnd}
                onChange={(e) => setSalesRange(salesStart, e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="text-xs text-gray-500 sm:text-right">
              <div className="mt-1 font-medium text-gray-700">
                Mostrando ventas entre {formatYmdDisplay(salesStart)} y {formatYmdDisplay(salesEnd)}.
              </div>
              Rangos con fecha ‘date’ o ‘fecha’ entre 00:00 y 23:59.
            </div>
          </div>
          </div>
        )}
      </div>

      {(productsLoading || isPending || salesLoading) && (
        <div className="h-1 bg-blue-200 animate-pulse rounded my-2" />
      )}

      {deleteConfirm?.open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-3 sm:items-center sm:p-4">
          <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar producto</h3>
            <p className="text-sm text-gray-700 mb-4">
              ¿Estás seguro de eliminar <span className="font-medium">{deleteConfirm.product?.name || 'este producto'}</span>? Dejará de aparecer en los demás equipos.
            </p>
            {deleteConfirm.checking && (
              <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                Verificando si este producto se utiliza en alguna receta…
              </div>
            )}
            {deleteConfirm.dependencies.length > 0 && (
              <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                <div className="font-semibold">
                  No se puede eliminar: se utiliza en {deleteConfirm.dependencies.length} receta{deleteConfirm.dependencies.length === 1 ? '' : 's'}.
                </div>
                <div className="mt-1">Primero reemplaza este insumo en los siguientes productos:</div>
                <ul className="mt-2 max-h-40 list-disc overflow-y-auto pl-5">
                  {deleteConfirm.dependencies.slice(0, 12).map(dependency => (
                    <li key={dependency.id}>
                      {dependency.name}
                      {dependency.referenceCount > 1 ? ` (${dependency.referenceCount} usos)` : ''}
                    </li>
                  ))}
                </ul>
                {deleteConfirm.dependencies.length > 12 && (
                  <div className="mt-2 font-medium">
                    Y {deleteConfirm.dependencies.length - 12} receta{deleteConfirm.dependencies.length - 12 === 1 ? ' más' : 's más'}.
                  </div>
                )}
              </div>
            )}
            {deleteConfirm.error && (
              <div className="text-red-600 text-sm mb-3">{deleteConfirm.error}</div>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleteConfirm.loading}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={
                  deleteConfirm.loading
                  || deleteConfirm.checking
                  || deleteConfirm.dependencyCheckFailed
                  || deleteConfirm.dependencies.length > 0
                }
                className="w-full rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50 sm:w-auto"
              >
                {deleteConfirm.loading
                  ? 'Eliminando…'
                  : deleteConfirm.checking
                    ? 'Verificando…'
                    : deleteConfirm.dependencies.length > 0
                      ? 'Eliminación bloqueada'
                      : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => loadMoreProducts()}
            disabled={productsLoading}
            className="px-4 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-60"
          >
            Cargar mas productos
          </button>
        </div>
      )}

      {renderSubView()}
    </div>
  </div>
);
};

export default ProductsView;
